import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Calendar, Filter, Search, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import Header from '../../../components/ui/Header';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const RegulatoryReports = () => {
  const navigate = useNavigate();
  const [user] = useState({
    id: 'admin-001',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@moneytransfer.com',
    role: 'admin'
  });

  const [selectedReportType, setSelectedReportType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('month');

  const handleLogout = () => {
    navigate('/login');
  };

  const reportTypes = [
    { value: 'all', label: 'All Reports' },
    { value: 'sar', label: 'Suspicious Activity Reports (SAR)' },
    { value: 'ctr', label: 'Currency Transaction Reports (CTR)' },
    { value: 'kyc', label: 'KYC/AML Compliance' },
    { value: 'sanctions', label: 'Sanctions Screening' },
    { value: 'pep', label: 'PEP Monitoring' },
    { value: 'transaction', label: 'Transaction Monitoring' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending Review' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'approved', label: 'Approved' },
    { value: 'flagged', label: 'Flagged' }
  ];

  const dateRanges = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  // Mock statistics
  const stats = [
    {
      title: 'Total Reports',
      value: '1,234',
      change: '+12%',
      changeType: 'positive',
      icon: FileText,
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      title: 'Pending Review',
      value: '47',
      change: '+5',
      changeType: 'neutral',
      icon: Clock,
      color: 'text-warning',
      bgColor: 'bg-warning/10'
    },
    {
      title: 'Flagged Items',
      value: '23',
      change: '-8%',
      changeType: 'positive',
      icon: AlertTriangle,
      color: 'text-error',
      bgColor: 'bg-error/10'
    },
    {
      title: 'Compliance Rate',
      value: '98.5%',
      change: '+0.5%',
      changeType: 'positive',
      icon: CheckCircle,
      color: 'text-success',
      bgColor: 'bg-success/10'
    }
  ];

  // Mock reports data
  const reports = [
    {
      id: 'SAR-2024-001',
      type: 'SAR',
      title: 'Suspicious Activity - Large Cash Deposits',
      description: 'Multiple large cash deposits detected over short period',
      submittedBy: 'John Smith',
      submittedDate: '2024-11-18',
      dueDate: '2024-11-25',
      status: 'pending',
      priority: 'high',
      customerId: 'CUST-789',
      amount: '$45,000'
    },
    {
      id: 'CTR-2024-045',
      type: 'CTR',
      title: 'Currency Transaction Report',
      description: 'Transaction exceeding $10,000 threshold',
      submittedBy: 'Sarah Johnson',
      submittedDate: '2024-11-17',
      dueDate: '2024-11-24',
      status: 'submitted',
      priority: 'medium',
      customerId: 'CUST-456',
      amount: '$12,500'
    },
    {
      id: 'KYC-2024-089',
      type: 'KYC',
      title: 'Enhanced Due Diligence Report',
      description: 'High-risk customer requiring additional verification',
      submittedBy: 'Michael Rodriguez',
      submittedDate: '2024-11-16',
      dueDate: '2024-11-23',
      status: 'approved',
      priority: 'high',
      customerId: 'CUST-123',
      amount: 'N/A'
    },
    {
      id: 'SANC-2024-012',
      type: 'Sanctions',
      title: 'Sanctions Screening Hit',
      description: 'Potential match with OFAC sanctions list',
      submittedBy: 'Emily Chen',
      submittedDate: '2024-11-15',
      dueDate: '2024-11-22',
      status: 'flagged',
      priority: 'critical',
      customerId: 'CUST-901',
      amount: '$8,200'
    },
    {
      id: 'PEP-2024-034',
      type: 'PEP',
      title: 'PEP Monitoring Report',
      description: 'Politically Exposed Person identified in transaction',
      submittedBy: 'David Kim',
      submittedDate: '2024-11-14',
      dueDate: '2024-11-21',
      status: 'pending',
      priority: 'high',
      customerId: 'CUST-567',
      amount: '$25,000'
    }
  ];

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: 'bg-warning/10 text-warning border-warning/20',
      submitted: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      approved: 'bg-success/10 text-success border-success/20',
      flagged: 'bg-error/10 text-error border-error/20'
    };

    return statusStyles?.[status] || statusStyles?.pending;
  };

  const getPriorityBadge = (priority) => {
    const priorityStyles = {
      critical: 'bg-error text-white',
      high: 'bg-warning text-white',
      medium: 'bg-blue-500 text-white',
      low: 'bg-muted text-foreground'
    };

    return priorityStyles?.[priority] || priorityStyles?.medium;
  };

  const handleGenerateReport = () => {
    console.log('Generating new report...');
    alert('Report generation initiated');
  };

  const handleDownloadReport = (reportId) => {
    console.log(`Downloading report: ${reportId}`);
    alert(`Downloading ${reportId}`);
  };

  const handleViewReport = (reportId) => {
    console.log(`Viewing report: ${reportId}`);
    // Navigate to detailed report view
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} onLogout={handleLogout} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin-dashboard')}
            className="flex items-center text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Admin Dashboard
          </button>
          
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Regulatory Reports</h1>
              <p className="text-muted-foreground mt-2">
                Monitor compliance reports and regulatory submissions
              </p>
            </div>
            
            <Button
              onClick={handleGenerateReport}
              className="bg-primary hover:bg-primary/90"
            >
              <FileText className="w-4 h-4 mr-2" />
              Generate Report
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats?.map((stat, idx) => (
            <div key={idx} className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">{stat?.title}</p>
                  <p className="text-3xl font-bold text-foreground mt-2">{stat?.value}</p>
                  <p className={`text-sm mt-2 ${
                    stat?.changeType === 'positive' ? 'text-success' : 
                    stat?.changeType === 'negative'? 'text-error' : 'text-muted-foreground'
                  }`}>
                    {stat?.change} from last period
                  </p>
                </div>
                <div className={`${stat?.bgColor} p-3 rounded-lg`}>
                  <stat.icon className={`w-6 h-6 ${stat?.color}`} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-card rounded-lg border border-border p-6 mb-6">
          <div className="flex items-center mb-4">
            <Filter className="w-5 h-5 text-muted-foreground mr-2" />
            <h3 className="text-lg font-semibold text-foreground">Filters</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search reports..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="pl-10"
              />
            </div>
            
            <Select
              value={selectedReportType}
              onChange={(e) => setSelectedReportType(e?.target?.value)}
            >
              {reportTypes?.map((type) => (
                <option key={type?.value} value={type?.value}>
                  {type?.label}
                </option>
              ))}
            </Select>
            
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e?.target?.value)}
            >
              {statusOptions?.map((status) => (
                <option key={status?.value} value={status?.value}>
                  {status?.label}
                </option>
              ))}
            </Select>
            
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e?.target?.value)}
            >
              {dateRanges?.map((range) => (
                <option key={range?.value} value={range?.value}>
                  {range?.label}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {/* Reports Table */}
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">Report ID</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">Type</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">Title</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">Priority</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">Status</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">Due Date</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">Amount</th>
                  <th className="text-left py-4 px-6 text-sm font-semibold text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {reports?.map((report) => (
                  <tr key={report?.id} className="hover:bg-muted/30 transition-colors">
                    <td className="py-4 px-6">
                      <span className="font-mono text-sm text-foreground">{report?.id}</span>
                    </td>
                    <td className="py-4 px-6">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        {report?.type}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div>
                        <p className="text-sm font-medium text-foreground">{report?.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">{report?.description}</p>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityBadge(report?.priority)}`}>
                        {report?.priority?.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(report?.status)}`}>
                        {report?.status?.charAt(0)?.toUpperCase() + report?.status?.slice(1)}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center text-sm text-foreground">
                        <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
                        {report?.dueDate}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="text-sm font-medium text-foreground">{report?.amount}</span>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewReport(report?.id)}
                          className="text-xs"
                        >
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadReport(report?.id)}
                          className="text-xs"
                        >
                          <Download className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing 1 to {reports?.length} of {reports?.length} reports
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled>
              Previous
            </Button>
            <Button variant="outline" size="sm">
              Next
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RegulatoryReports;